import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useStore } from 'zustand';

import { api, unwrap, type Schemas } from '@/lib/api';
import { sessionStore } from '@/lib/auth/session';

/**
 * Organisation-scoped reads for the shell. Every key starts with the org id so a
 * switch of org never shows another org's cache, and one invalidation
 * (`orgKeys.root(orgId)`) refreshes everything on screen.
 */
export type Org = Schemas['OrgOut'];
export type Connector = Schemas['ConnectorOut'];
export type ConnectorsOut = Schemas['ConnectorsOut'];
export type Balance = Schemas['BalanceOut'];
export type OrgSummary = Schemas['OrgSummary'];
export type OrgPatch = Schemas['OrgPatch'];

export const orgKeys = {
  root: (orgId: string) => ['org', orgId] as const,
  me: (orgId: string) => ['org', orgId, 'me'] as const,
  connectors: (orgId: string) => ['org', orgId, 'connectors'] as const,
  credits: (orgId: string) => ['org', orgId, 'credits'] as const,
};

export const authKeys = {
  myOrganizations: ['me', 'organizations'] as const,
};

export function orgQuery(orgId: string) {
  return queryOptions({
    queryKey: orgKeys.me(orgId),
    queryFn: async () => unwrap(await api.GET('/api/v2/organizations/me')),
  });
}

export function connectorsQuery(orgId: string) {
  return queryOptions({
    queryKey: orgKeys.connectors(orgId),
    queryFn: async () => unwrap(await api.GET('/api/v2/organizations/me/connectors')),
  });
}

export function creditsQuery(orgId: string) {
  return queryOptions({
    queryKey: orgKeys.credits(orgId),
    queryFn: async () => unwrap(await api.GET('/api/v2/credits/balance')),
    staleTime: 30_000,
  });
}

export function myOrganizationsQuery() {
  return queryOptions({
    queryKey: authKeys.myOrganizations,
    queryFn: async () => unwrap(await api.GET('/api/v2/auth/me/organizations')),
    staleTime: 5 * 60_000,
  });
}

export const useOrg = (orgId: string) => useQuery(orgQuery(orgId));
export const useConnectors = (orgId: string) => useQuery(connectorsQuery(orgId));
export const useCredits = (orgId: string) => useQuery(creditsQuery(orgId));
export const useMyOrganizations = (enabled: boolean) => useQuery({ ...myOrganizationsQuery(), enabled });

/**
 * PATCH /organizations/me. Only the fields sent change; metadata, brand_metadata and
 * benchmarks merge into the stored JSON. The org read is refreshed with the answer.
 */
export function useUpdateOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: OrgPatch) => unwrap(await api.PATCH('/api/v2/organizations/me', { body })),
    onSuccess: (org) => {
      qc.setQueryData(orgKeys.me(orgId), org);
    },
  });
}

/**
 * The current org id. Screens read the org through this hook only, so the org can
 * stay out of the URL. The /_app layout sets it before any child renders.
 */
export function useOrgId(): string {
  return useStore(sessionStore, (s) => s.orgId) ?? '';
}

/** Backend source name of a connector: "Meta Ads" → "meta_ads" (the backend resolves aliases). */
export function sourceName(connectorName: string): string {
  return connectorName
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

/**
 * The data sources the metrics routes read, from the enabled connectors.
 * `ready` is false until the connectors list has loaded; an empty list means
 * nothing is connected and screens show their empty state.
 */
export function useSources(orgId: string): { sources: string[]; ready: boolean } {
  const q = useConnectors(orgId);
  const connectors = q.data?.connectors;
  const sources = useMemo(
    () =>
      [
        ...new Set((connectors ?? []).filter((c) => c.is_enabled).map((c) => sourceName(c.connector_name))),
      ].sort(),
    [connectors],
  );
  return { sources, ready: q.isSuccess };
}
